import { useCallback, useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { Icone } from '../Icone';
import type { FichierPieceDossier } from '../../lib/useApprenants';
import type { CorpsErreurApi } from '../../lib/types';

type EtatApercu = { statut: 'chargement' } | { statut: 'ok' } | { statut: 'erreur'; message: string };

/**
 * Consultation des fichiers d'une pièce justificative, sans quitter le dossier.
 *
 * Vérifier une pièce suppose de la voir. Le fichier n'est jamais servi en
 * statique : il passe par la route gardée, qui vérifie le profil et le
 * rattachement au site, trace l'accès et interdit toute mise en cache.
 *
 * Le contenu vient d'un tiers — un candidat dépose ce qu'il veut — et il est
 * donc affiché sous contrainte. C'est la **réponse de l'API** qui la pose, avec
 * `Content-Security-Policy: sandbox allow-scripts` : le document se retrouve
 * dans une origine opaque — `allow-same-origin` n'est pas accordé, donc ni
 * cookie de session, ni accès au DOM parent.
 *
 * `allow-scripts` est indispensable, et c'est contre-intuitif : le lecteur PDF
 * de Chrome est lui-même une application JavaScript. Un bac à sable qui interdit
 * les scripts — l'en-tête `sandbox` seul, comme l'attribut `sandbox` de
 * l'`iframe` — lui fait refuser la page : « Cette page a été bloquée par
 * Chrome ». Une source `blob:` y serait refusée de la même façon.
 *
 * Le HEIC des iPhone n'est affichable par aucun navigateur de bureau : le
 * fichier est alors proposé au téléchargement plutôt que présenté comme
 * illisible.
 *
 * Recto et verso d'une même pièce se parcourent aux flèches du clavier comme
 * aux boutons : c'est le geste de vérification, il ne doit pas obliger à
 * refermer la fenêtre entre deux fichiers.
 *
 * `telechargeable={false}` (pôle facturation, TF-464) : consultation à l'écran
 * seulement — ni bouton « Télécharger », ni barre d'outils du lecteur PDF.
 * L'API refuse de toute façon la réponse en pièce jointe. Ce n'est qu'un
 * frein : ce qui s'affiche peut toujours se copier.
 */
export function VisionneusePiece({
  ouverte,
  libellePiece,
  fichiers,
  indexInitial = 0,
  pieceId,
  urlFichier,
  urlBase,
  nomFichier,
  telechargeable = true,
  onFermer,
}: {
  ouverte: boolean;
  libellePiece: string;
  fichiers?: FichierPieceDossier[];
  /** Fichier ouvert au départ, celui sur lequel on a cliqué. */
  indexInitial?: number;
  pieceId?: string;
  urlFichier?: (fichierId: string) => string;
  /** Pour consulter un document direct (ex: compte-rendu d'entretien ou document généré). */
  urlBase?: string;
  nomFichier?: string;
  /** Faux : consultation seule, sans téléchargement (TF-464). */
  telechargeable?: boolean;
  onFermer: () => void;
}): JSX.Element {
  const [index, setIndex] = useState(indexInitial);

  const listeFichiers: (FichierPieceDossier | { id: string; nomFichier: string; typeMime: string })[] =
    fichiers && fichiers.length > 0
      ? fichiers
      : urlBase
        ? [
            {
              id: 'doc-apercu',
              nomFichier: nomFichier ?? 'document.pdf',
              typeMime:
                nomFichier && /\.(png|jpe?g|webp)$/i.test(nomFichier)
                  ? 'image/jpeg'
                  : 'application/pdf',
            },
          ]
        : [];

  useEffect(() => {
    if (ouverte) setIndex(indexInitial);
  }, [ouverte, indexInitial]);

  const precedent = useCallback(
    () => setIndex((actuel) => (actuel > 0 ? actuel - 1 : actuel)),
    [],
  );
  const suivant = useCallback(
    () => setIndex((actuel) => (actuel < listeFichiers.length - 1 ? actuel + 1 : actuel)),
    [listeFichiers.length],
  );

  useEffect(() => {
    if (!ouverte || listeFichiers.length < 2) return;
    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'ArrowLeft') precedent();
      if (evenement.key === 'ArrowRight') suivant();
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [ouverte, listeFichiers.length, precedent, suivant]);

  // Empêche les déclenchements automatiques intempestifs de la boîte d'impression
  // (ex. PDF embarqué avec action JS automatique ou lecteur du navigateur).
  useEffect(() => {
    if (!ouverte) return;
    const printOriginal = window.print;
    window.print = () => {
      // Ignorer l'appel d'impression automatique non sollicité pendant la consultation
    };
    return () => {
      window.print = printOriginal;
    };
  }, [ouverte]);

  const fichier = listeFichiers[index];

  const adresseFichier = (fichierId: string): string => {
    if (urlFichier) return urlFichier(fichierId);
    return pieceId ? `/api/pieces/${pieceId}/fichiers/${fichierId}` : '';
  };
  const base = urlBase ?? (fichier ? adresseFichier(fichier.id) : '');
  const separateur = base.includes('?') ? '&' : '?';
  const affichable = base ? `${base}${separateur}inline=1` : '';
  const nomDocument = nomFichier ?? fichier?.nomFichier ?? 'document.pdf';
  const estImage = !urlBase && !!fichier && fichier.typeMime.startsWith('image/') && !estHeic(fichier.typeMime);
  const estPdf = urlBase ? !estImage : fichier?.typeMime === 'application/pdf';

  const [etat, setEtat] = useState<EtatApercu>({ statut: 'chargement' });

  // `<img>`/`<iframe>` ne signalent pas un 404 : une image cassée se voit, un
  // PDF absent ouvre une page d'erreur muette dans le lecteur du navigateur.
  // Une pièce de démonstration (seed) ou un fichier disparu du stockage
  // doivent se lire comme « aperçu indisponible », pas comme un bouton mort.
  // On sonde donc la route avant de monter l'`<img>`/`<iframe>`, en annulant
  // le téléchargement dès que le statut est connu pour ne pas doubler le
  // transfert du fichier affiché ensuite par sa propre balise.
  useEffect(() => {
    if (!base) return;
    let annule = false;
    const controleur = new AbortController();
    setEtat({ statut: 'chargement' });

    fetch(affichable, { credentials: 'include', signal: controleur.signal })
      .then(async (reponse) => {
        if (reponse.ok) {
          controleur.abort();
          if (!annule) setEtat({ statut: 'ok' });
          return;
        }
        let message = 'Aperçu indisponible.';
        try {
          const corps = (await reponse.json()) as CorpsErreurApi;
          if (corps.erreur?.message) message = corps.erreur.message;
        } catch {
          // Corps non JSON : le message par défaut suffit.
        }
        if (!annule) setEtat({ statut: 'erreur', message });
      })
      .catch((erreur) => {
        if (annule || (erreur instanceof DOMException && erreur.name === 'AbortError')) return;
        setEtat({ statut: 'erreur', message: 'Aperçu indisponible : la connexion a échoué.' });
      });

    return () => {
      annule = true;
      controleur.abort();
    };
  }, [affichable, base]);

  if (!fichier && !urlBase) return <></>;

  return (
    <Modale
      titre={libellePiece}
      sousTitre={
        listeFichiers.length > 1
          ? `${nomDocument} · fichier ${index + 1} sur ${listeFichiers.length}`
          : nomDocument
      }
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-5xl"
    >
      <div className="space-y-3 px-4 py-4 sm:px-6">
        {/* Hauteur en unités de fenêtre : le document occupe la place
            disponible sur tablette comme sur grand écran, sans jamais pousser
            les actions hors de vue. */}
        <div className="relative flex min-h-[50vh] items-center justify-center overflow-auto rounded-xl bg-codialis-sand p-2 sm:min-h-[60vh]">
          {etat.statut === 'chargement' && (
            <p className="text-xs text-codialis-gray">Vérification du fichier…</p>
          )}

          {etat.statut === 'erreur' && (
            <div className="max-w-sm p-4 sm:p-6 text-center">
              <Icone nom="document" className="mx-auto h-8 w-8 text-codialis-gray" />
              <p className="mt-2 text-sm font-medium text-codialis-navy">Aperçu indisponible</p>
              <p className="mt-1 text-xs text-codialis-gray">{etat.message}</p>
            </div>
          )}

          {etat.statut === 'ok' && estImage && (
            // Fond blanc sous l'image, et non le sable de la carte : un scan de
            // document est très majoritairement blanc, ses bords se
            // confondraient avec le fond et on ne saurait plus où il s'arrête.
            //
            // La clé force le remplacement de l'élément d'un fichier à l'autre :
            // sans elle, React réutilise la même balise et l'aperçu précédent
            // reste affiché pendant le chargement du suivant.
            <img
              key={fichier ? fichier.id : base}
              src={affichable}
              alt={`Aperçu de ${nomDocument}`}
              className="max-h-[70vh] w-auto max-w-full rounded-lg bg-white object-contain shadow-sm"
            />
          )}

          {etat.statut === 'ok' && estPdf && (
            // Pas d'attribut `sandbox` : Chrome refuse d'ouvrir son lecteur PDF
            // dans une iframe bac à sable, quelles que soient les permissions
            // accordées — « Cette page a été bloquée par Chrome ». Le
            // confinement du document reste assuré côté serveur, par l'en-tête
            // `Content-Security-Policy: sandbox` de la route de téléchargement
            // (origine opaque, ni script ni cookie de session) et par
            // `X-Content-Type-Options: nosniff`.
            <iframe
              key={fichier ? fichier.id : base}
              // `#toolbar=0` masque la barre du lecteur PDF de Chrome et Edge
              // (enregistrer, imprimer) pour qui ne télécharge pas.
              src={telechargeable ? affichable : `${affichable}#toolbar=0`}
              title={`Aperçu de ${nomDocument}`}
              className="h-[70vh] w-full rounded-lg border-0 bg-white"
            />
          )}

          {etat.statut === 'ok' && !estImage && !estPdf && (
            <div className="max-w-sm p-4 sm:p-6 text-center">
              <Icone nom="document" className="mx-auto h-8 w-8 text-codialis-gray" />
              <p className="mt-2 text-sm font-medium text-codialis-navy">
                Ce format ne s’affiche pas dans le navigateur
              </p>
              <p className="mt-1 text-xs text-codialis-gray">
                {!telechargeable
                  ? 'Il ne se lit qu’après téléchargement : demandez-le au pôle administratif.'
                  : estHeic(fichier?.typeMime ?? '')
                    ? 'Les photos HEIC des iPhone ne sont lisibles qu’après téléchargement.'
                    : 'Téléchargez le fichier pour le consulter.'}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {listeFichiers.length > 1 && (
              <>
                <BoutonNavigation
                  libelle="Fichier précédent"
                  desactive={index === 0}
                  onClick={precedent}
                  chemin="M15 19l-7-7 7-7"
                />
                <span className="text-xs text-codialis-gray">
                  {index + 1} / {listeFichiers.length}
                </span>
                <BoutonNavigation
                  libelle="Fichier suivant"
                  desactive={index === listeFichiers.length - 1}
                  onClick={suivant}
                  chemin="M9 5l7 7-7 7"
                />
              </>
            )}
          </div>

          {/* Sans `?inline=1`, la même route répond en `attachment` : c'est le
              téléchargement, sous le nom d'origine du fichier.
              Si la pièce est absente du stockage, le bouton est désactivé
              pour ne pas provoquer d'erreur ou d'effet indésirable du navigateur. */}
          {!telechargeable ? (
            <span className="text-xs text-codialis-gray">
              Consultation à l’écran · téléchargement réservé au pôle administratif
            </span>
          ) : etat.statut === 'ok' ? (
            <a
              href={base}
              download={nomDocument}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <Icone nom="telecharger" className="h-4 w-4" />
              Télécharger
            </a>
          ) : (
            <button
              type="button"
              disabled
              title={etat.statut === 'erreur' ? etat.message : 'Chargement du fichier…'}
              className="inline-flex min-h-[44px] cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm font-semibold text-codialis-gray opacity-60"
            >
              <Icone nom="telecharger" className="h-4 w-4" />
              {etat.statut === 'erreur' ? 'Téléchargement indisponible' : 'Vérification…'}
            </button>
          )}
        </div>
      </div>
    </Modale>
  );
}

/** Le HEIC est accepté au dépôt mais aucun navigateur de bureau ne l'affiche. */
function estHeic(typeMime: string): boolean {
  return typeMime === 'image/heic' || typeMime === 'image/heif';
}

function BoutonNavigation({
  libelle,
  desactive,
  onClick,
  chemin,
}: {
  libelle: string;
  desactive: boolean;
  onClick: () => void;
  chemin: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desactive}
      aria-label={libelle}
      // 44 px : cible tactile recommandée, le dossier se consulte sur tablette.
      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={chemin} />
      </svg>
    </button>
  );
}
